import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface ContactFilters {
  search?: string;
  origine?: string;
  email_validity?: 'valid' | 'invalid';
  user_id?: number;
  start_date?: string;
  end_date?: string;
  statut_du_lead?: string;
  type_contact?: string;
  cycle_de_vie?: string;
}

export interface ContactOwner {
  id?: number;
  email?: string;
  username?: string;
}

export interface Contact {
  id: number;
  reference?: string;
  user_id: number;
  user?: ContactOwner | null;

  // Identité Assuré 1
  type_personne?: string;
  civilite?: string;
  nom?: string;
  prenom?: string;
  date_naissance?: string;
  etat_civil?: string;

  // Coordonnées Assuré 1
  mobile?: string;
  fixe?: string;
  email?: string;
  email_is_valid?: boolean | null;
  adresse?: string;
  complement_adresse?: string;
  ville?: string;
  code_postal?: string;
  pays?: string;

  // CRM / Lead
  cycle_de_vie?: string;
  entreprise?: string;
  site_web?: string;
  linkedin?: string;
  profession_exacte?: string;
  origine_du_lead?: string;
  statut_du_lead?: string;
  avis_client?: boolean;
  type_contact?: string;
  origine_secondaire_du_lead?: string;
  statut_socioprofessionnel?: string;
  fumeur?: boolean;

  // Assuré 2
  est_assure2?: boolean;
  assure2_civilite?: string;
  assure2_nom?: string;
  assure2_prenom?: string;
  assure2_date_naissance?: string;
  assure2_etat_civil?: string;
  assure2_mobile?: string;
  assure2_fixe?: string;
  assure2_email?: string;
  assure2_email_is_valid?: boolean | null;
  assure2_adresse?: string;
  assure2_complement_adresse?: string;
  assure2_ville?: string;
  assure2_code_postal?: string;
  assure2_pays?: string;
  assure2_entreprise?: string;
  assure2_site_web?: string;
  assure2_linkedin?: string;
  assure2_profession_exacte?: string;
  assure2_type_contact?: string;
  assure2_statut_socioprofessionnel?: string;
  assure2_fumeur?: boolean;

  created_at?: string;
  updated_at?: string;
}

@Injectable({ providedIn: 'root' })
export class ContactService {
  private apiUrl = `${environment.apiUrl}${environment.apiEndpoint}/contacts`;

  constructor(private http: HttpClient) {}

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('access_token');
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });
  }

  /** Paramètres de filtrage, communs à `list` et `count` (mêmes filtres des deux
   *  côtés, sinon la pagination annonce un total qui ne colle pas aux lignes). */
  private buildFilterParams(search?: string, filters?: ContactFilters): HttpParams {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    if (filters?.origine)        params = params.set('origine', filters.origine);
    if (filters?.email_validity) params = params.set('email_validity', filters.email_validity);
    if (filters?.user_id != null) params = params.set('user_id', filters.user_id.toString());
    if (filters?.start_date)     params = params.set('start_date', filters.start_date);
    if (filters?.end_date)       params = params.set('end_date', filters.end_date);
    if (filters?.statut_du_lead) params = params.set('statut_du_lead', filters.statut_du_lead);
    if (filters?.type_contact)   params = params.set('type_contact', filters.type_contact);
    if (filters?.cycle_de_vie)   params = params.set('cycle_de_vie', filters.cycle_de_vie);
    return params;
  }

  list(limit = 100, offset = 0, search?: string, filters?: ContactFilters): Observable<Contact[]> {
    const params = this.buildFilterParams(search, filters)
      .set('limit', limit.toString())
      .set('offset', offset.toString());
    return this.http.get<Contact[]>(this.apiUrl, {
      params,
      headers: this.getAuthHeaders(),
    });
  }

  count(search?: string, filters?: ContactFilters): Observable<{ count: number }> {
    return this.http.get<{ count: number }>(`${this.apiUrl}/count`, {
      params: this.buildFilterParams(search, filters),
      headers: this.getAuthHeaders(),
    });
  }

  syncEmailValidation(contactIds: number[] = [], onlyMissing = true): Observable<{ requested_count: number; updated_count: number; items: any[] }> {
    return this.http.post<any>(`${this.apiUrl}/email-validation/sync`, {
      contact_ids: contactIds, only_missing: onlyMissing,
    }, { headers: this.getAuthHeaders() });
  }

  exportExcel(contactIds: number[] = [], options: Partial<ContactFilters> & { export_all_matching?: boolean } = {}): Observable<HttpResponse<Blob>> {
    const token = localStorage.getItem('access_token');
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    return this.http.post(`${this.apiUrl}/export/excel`, {
      contact_ids: contactIds,
      ...options,
    }, { headers, observe: 'response', responseType: 'blob' });
  }

  get(id: number): Observable<Contact> {
    return this.http.get<Contact>(`${this.apiUrl}/${id}`, {
      headers: this.getAuthHeaders(),
    });
  }

  create(payload: Partial<Contact>): Observable<Contact> {
    return this.http.post<Contact>(this.apiUrl, payload, {
      headers: this.getAuthHeaders(),
    });
  }

  update(id: number, payload: Partial<Contact>): Observable<Contact> {
    return this.http.patch<Contact>(`${this.apiUrl}/${id}`, payload, {
      headers: this.getAuthHeaders(),
    });
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`, {
      headers: this.getAuthHeaders(),
    });
  }
}
